import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Your web app's Firebase configuration
// Replace these with your actual Firebase config values
const firebaseConfig = {
  apiKey: "AIzaSyCszOek2WAeRGG-5isVaTAPZU-NKSuDp7A",
  authDomain: "cafelawn.firebaseapp.com",
  projectId: "cafelawn",
  storageBucket: "cafelawn.firebasestorage.app",
  messagingSenderId: "776036905136",
  appId: "1:776036905136:web:8d1bba54718c9cff4f0b75",
  measurementId: "G-24SV0N4KR7"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);
export const storage = getStorage(app);

// Generate next tenant ID
export const generateTenantId = async (): Promise<string> => {
  try {
    // Query the tenants collection to get the latest tenant ID
    const tenantsRef = collection(db, 'tenants');
    const q = query(tenantsRef, orderBy('createdAt', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);
    
    let nextNumber = 1;
    
    if (!querySnapshot.empty) {
      const lastTenant = querySnapshot.docs[0];
      const lastTenantId = lastTenant.id;
      // Extract number from T-000001 format
      const idParts = lastTenantId.split('-');
      if (idParts.length === 2 && idParts[0] === 'T') {
        const lastNumber = parseInt(idParts[1], 10);
        if (!isNaN(lastNumber)) {
          nextNumber = lastNumber + 1;
        }
      }
    }
    
    // Format as T-000001
    return `T-${nextNumber.toString().padStart(6, '0')}`;
  } catch (error) {
    console.error('Error generating tenant ID:', error);
    // Fallback to timestamp-based ID
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `T-${timestamp.toString().slice(-3)}${randomSuffix}`;
  }
};

// Create tenant document
export const createTenant = async (tenantId: string, tenantData: any) => {
  try {
    const tenantRef = doc(db, 'tenants', tenantId);
    await setDoc(tenantRef, {
      ...tenantData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    // Don't create subcollections here - they will be created when needed
    // This was causing permission issues during signup
    
    return tenantId;
  } catch (error) {
    console.error('Error creating tenant:', error);
    throw error;
  }
};

// Create user document
export const createUserDocument = async (userId: string, tenantId: string, userData: any) => {
  try {
    // Create user in main users collection
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...userData,
      tenantId,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    // Create user in tenant's users subcollection
    const tenantUserRef = doc(db, 'tenants', tenantId, 'users', userId);
    await setDoc(tenantUserRef, {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return userId;
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
};

// Get user with tenant information
export const getUserWithTenant = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const tenantRef = doc(db, 'tenants', userData.tenantId);
      const tenantSnap = await getDoc(tenantRef);
      
      return {
        user: { id: userSnap.id, ...userData },
        tenant: tenantSnap.exists() ? { id: tenantSnap.id, ...tenantSnap.data() } : null
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user with tenant:', error);
    throw error;
  }
};

// Get tenant by ID
export const getTenantById = async (tenantId: string) => {
  try {
    const tenantRef = doc(db, 'tenants', tenantId);
    const tenantSnap = await getDoc(tenantRef);
    
    if (tenantSnap.exists()) {
      return { id: tenantSnap.id, ...tenantSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting tenant by ID:', error);
    throw error;
  }
};

// Get tenant collection reference
export const getTenantCollection = (tenantId: string, collectionName: string) => {
  return collection(db, 'tenants', tenantId, collectionName);
};

// Get tenant document reference
export const getTenantDocument = (tenantId: string, collectionName: string, docId: string) => {
  return doc(db, 'tenants', tenantId, collectionName, docId);
};

export default app; 